<template>
  <div class="yield-forecast">
    <div class="metric-grid">
      <div v-for="item in metrics" :key="item.name" class="metric-card">
        <span>{{ item.name }}</span>
        <strong>{{ item.value }}<em>{{ item.unit }}</em></strong>
      </div>
    </div>
    <div class="chart-panel">
      <div class="panel-title">分支品类产量对比</div>
      <div ref="chartRef" class="chart"></div>
    </div>
  </div>
</template>

<script setup>
import * as echarts from "echarts";
import { onBeforeUnmount, onMounted, ref } from "vue";

const chartRef = ref(null);
let chartInstance = null;

const metrics = [
  { name: "粮食预估总产", value: "42.6", unit: "万吨" },
  { name: "特色农产", value: "18.2", unit: "万吨" },
  { name: "平均亩产", value: "412", unit: "公斤" },
  { name: "风险面积", value: "2.9", unit: "万亩" },
];

const cropNames = ["小麦", "玉米", "大豆", "红薯", "黄姜", "萝卜", "蔬菜"];
const thisYear = [
  { value: 410, labelText: "410kg" },
  { value: 520, labelText: "520kg" },
  { value: 186, labelText: "186kg" },
  { value: 260, labelText: "260kg" },
  { value: 380, labelText: "380kg" },
  { value: 450, labelText: "450kg" },
  { value: 280, labelText: "280kg" },
];
const average = [
  { value: 386, labelText: "386kg" },
  { value: 488, labelText: "488kg" },
  { value: 168, labelText: "168kg" },
  { value: 238, labelText: "238kg" },
  { value: 352, labelText: "352kg" },
  { value: 418, labelText: "418kg" },
  { value: 252, labelText: "252kg" },
];

function createOption() {
  return {
    color: ["#0d5b66", "#2ef6f7"],
    grid: { top: 100, left: 86, right: 42, bottom: 50 },
    legend: {
      top: 26,
      right: 28,
      itemWidth: 20,
      itemHeight: 10,
      itemGap: 20,
      textStyle: { color: "#e1fbff", fontSize: 24, fontWeight: 700 },
      data: ["本年预测", "历年平均"],
    },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      backgroundColor: "rgba(2, 31, 45, .9)",
      borderColor: "rgba(70, 244, 248, .55)",
      textStyle: { color: "#fff", fontSize: 22 },
    },
    xAxis: {
      type: "category",
      data: cropNames,
      axisTick: { show: false },
      axisLine: { lineStyle: { color: "rgba(126, 221, 234, .45)" } },
      axisLabel: { color: "#e8fbff", fontSize: 22, fontWeight: 700, interval: 0 },
    },
    yAxis: {
      type: "value",
      max: 560,
      splitNumber: 6,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: "#d7f6ff",
        fontSize: 22,
        formatter: (value) => (value === 0 ? "" : value),
      },
      splitLine: { lineStyle: { color: "rgba(113, 220, 234, .4)", type: "dashed" } },
    },
    series: [
      {
        name: "本年预测",
        type: "bar",
        barWidth: 28,
        barGap: "50%",
        barCategoryGap: "28%",
        itemStyle: {
          color: "#0c5a64",
          borderRadius: [2, 2, 0, 0],
        },
        label: {
          show: false,
          position: "top",
          color: "#d9f8ff",
          fontSize: 22,
          fontWeight: 700,
          formatter: ({ data }) => data?.labelText || "",
        },
        data: thisYear,
      },
      {
        name: "历年平均",
        type: "bar",
        barWidth: 28,
        itemStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: "#40fbff" },
            { offset: 0.54, color: "#27e8eb" },
            { offset: 1, color: "#13959e" },
          ]),
          borderRadius: [2, 2, 0, 0],
        },
        label: {
          show: false,
          position: "top",
          color: "#d9f8ff",
          fontSize: 22,
          fontWeight: 700,
          formatter: ({ data }) => data?.labelText || "",
        },
        data: average,
      },
    ],
  };
}

function resizeChart() {
  chartInstance && chartInstance.resize();
}

onMounted(() => {
  chartInstance = echarts.init(chartRef.value);
  chartInstance.setOption(createOption());
  window.addEventListener("resize", resizeChart);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", resizeChart);
  chartInstance && chartInstance.dispose();
});
</script>

<style lang="less" scoped>
.yield-forecast {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-rows: 360px 1fr;
  gap: 42px;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px 42px;
  padding: 0 12px;
  box-sizing: border-box;
}

.metric-card {
  position: relative;
  min-width: 0;
  padding: 24px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(36, 155, 142, 0.8);
  background:
    linear-gradient(135deg, rgba(50, 251, 253, 0.18), transparent 24%) left top / 120px 80px no-repeat,
    linear-gradient(315deg, rgba(50, 251, 253, 0.15), transparent 22%) right bottom / 120px 80px no-repeat,
    radial-gradient(circle at 50% 45%, rgba(23, 158, 167, 0.34), rgba(2, 39, 55, 0.76) 64%);
  box-shadow:
    inset 0 0 34px rgba(19, 226, 231, 0.24),
    0 0 16px rgba(20, 217, 225, 0.2);

  &::before,
  &::after {
    content: "";
    position: absolute;
    width: 40px;
    height: 40px;
    border-color: rgba(36, 155, 142, 1.0);
    border-style: solid;
  }

  &::before {
    left: -1px;
    top: -1px;
    border-width: 4px 0 0 4px;
  }

  &::after {
    right: -1px;
    bottom: -1px;
    border-width: 0 4px 4px 0;
  }

  span {
    color: #e9fbff;
    font-size: 30px;
    font-weight: 700;
  }

  strong {
    margin-top: 8px;
    color: #55faff;
    font-size: 62px;
    line-height: 68px;
    // text-shadow: 0 0 14px rgba(50, 244, 255, 0.76);
     background: linear-gradient(to bottom, #B5FFF9, #74FFF9);
    -webkit-background-clip: text;
    color: transparent;

    em {
      margin-left: 6px;
      color: #e9fbff;
      font-size: 24px;
      font-style: normal;
    }
  }
}

.chart-panel {
  position: relative;
  min-height: 0;
  border: 1px solid rgba(36, 155, 142, 0.8);
  background:
    radial-gradient(circle at 50% 0, rgba(20, 144, 154, 0.2), rgba(3, 33, 51, 0.44) 46%),
    linear-gradient(180deg, rgba(2, 35, 53, 0.82), rgba(2, 31, 46, 0.72));
  box-shadow:
    inset 0 0 38px rgba(19, 200, 218, 0.12),
    0 0 16px rgba(16, 209, 223, 0.12);

  &::before,
  &::after {
    content: "";
    position: absolute;
    width: 42px;
    height: 42px;
    border-color: rgba(36, 155, 142, 1.0);
    border-style: solid;
  }

  &::before {
    left: -1px;
    top: -1px;
    border-width: 4px 0 0 4px;
  }

  &::after {
    right: -1px;
    bottom: -1px;
    border-width: 0 4px 4px 0;
  }
}

.panel-title {
  position: absolute;
  left: 36px;
  top: 30px;
  z-index: 2;
  padding-left: 18px;
  color: #f2fbff;
  font-size: 28px;
  font-weight: 700;
  line-height: 32px;
  border-left: 4px solid #36eff3;
}

.chart {
  width: 100%;
  height: 100%;
}
</style>
